import { useState } from 'react';
import { parseAmount } from '@domain';
import type { DeliveryInput, TrainingCourse, TrainingDelivery } from '../api.js';
import type { Translate } from '../i18n/index.js';
import { theme } from '../theme.js';
import { Card, PageTitle, Pill } from '../components/ui.js';

/**
 * The runs. One row per time a course is actually taught.
 *
 * This is the thing profit will eventually be measured against, and the thing
 * a client creates most often, so the list is the screen and the form opens
 * above it rather than on a page of its own.
 */

const PHASE_PILL: Record<string, { status: string; label: string }> = {
  upcoming: { status: 'DRAFT', label: 'Upcoming' },
  running: { status: 'READY', label: 'Running now' },
  finished: { status: 'PAID', label: 'Finished' },
  cancelled: { status: 'VOIDED', label: 'Cancelled' },
};

function blank(courseId: number): DeliveryInput {
  const today = new Date().toISOString().slice(0, 10);
  return {
    deliveryId: null,
    courseId,
    name: '',
    code: '',
    kind: 'public',
    startsOn: today,
    endsOn: null,
    trainer: '',
    venue: '',
    capacity: null,
    companyContactId: null,
    contractFee: null,
    status: 'planned',
    notes: '',
  };
}

export function DeliveriesScreen({
  courses,
  deliveries,
  loading,
  busy,
  error,
  t,
  onSave,
  onOpen,
}: {
  courses: TrainingCourse[];
  deliveries: TrainingDelivery[];
  loading: boolean;
  busy: boolean;
  error: string | null;
  t: Translate;
  onSave: (delivery: DeliveryInput) => void;
  onOpen: (deliveryId: number) => void;
}) {
  const offered = courses.filter((course) => course.active);
  const [editing, setEditing] = useState<DeliveryInput | null>(null);

  const field = {
    height: '34px',
    width: '100%',
    border: `1px solid ${theme.borderStrong}`,
    borderRadius: theme.radiusControl,
    padding: '0 10px',
    fontSize: '13px',
    background: '#fff',
  };

  const label = { display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' };

  const cell = {
    padding: '10px 12px',
    fontSize: '13px',
    borderBottom: `1px solid ${theme.dividerSoft}`,
  };

  const heading = {
    padding: '9px 12px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '.04em',
    color: theme.label,
    borderBottom: `1px solid ${theme.border}`,
    textAlign: 'start' as const,
  };

  const corporate = editing?.kind === 'corporate';
  const ready =
    editing !== null && editing.name.trim() !== '' && editing.courseId > 0 && !busy;

  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '1040px' }}>
      <PageTitle
        title={t('Training deliveries')}
        sub={t('Every time a course actually runs. Open one to manage who is on it.')}
      />
      <div style={{ height: '16px' }} />

      {error && (
        <Card style={{ marginBottom: '14px' }}>
          <div
            style={{ padding: '13px 16px', color: theme.badDeep, fontWeight: 600, fontSize: '13px' }}
          >
            {t(error)}
          </div>
        </Card>
      )}

      {offered.length === 0 && !loading && (
        <Card style={{ marginBottom: '14px' }}>
          <div style={{ padding: '14px 16px', fontSize: '13px', color: theme.textMuted }}>
            {t('Add a course first — a delivery is one run of a course.')}
          </div>
        </Card>
      )}

      {editing === null && offered.length > 0 && (
        <button
          type="button"
          onClick={() => setEditing(blank(offered[0]!.courseId))}
          style={{
            background: theme.nav,
            color: '#fff',
            border: 0,
            borderRadius: theme.radiusControl,
            padding: '10px 16px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            marginBottom: '14px',
          }}
        >
          + {t('Schedule a delivery')}
        </button>
      )}

      {editing !== null && (
        <Card style={{ marginBottom: '14px' }}>
          <div
            style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${theme.dividerSoft}`,
              fontWeight: 600,
              fontSize: '13px',
              background: theme.surfaceHeader,
            }}
          >
            {editing.deliveryId === null ? t('Schedule a delivery') : t('Edit delivery')}
          </div>

          <div style={{ padding: '16px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
              <label style={{ flex: '1 1 240px', minWidth: 0 }}>
                <span style={label}>{t('Course')}</span>
                <select
                  value={editing.courseId}
                  onChange={(e) => {
                    const courseId = Number(e.target.value);
                    const course = courses.find((c) => c.courseId === courseId);
                    setEditing({
                      ...editing,
                      courseId,
                      // Offer a name rather than demand one. "IFRS update"
                      // three times in a list tells the reader nothing, and
                      // the date is what distinguishes them.
                      name:
                        editing.name.trim() === '' && course
                          ? `${course.name} — ${editing.startsOn}`
                          : editing.name,
                    });
                  }}
                  style={field}
                >
                  {offered.map((course) => (
                    <option key={course.courseId} value={course.courseId}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </label>

              <label style={{ flex: '1 1 260px', minWidth: 0 }}>
                <span style={label}>{t('What to call this run')}</span>
                <input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder={t('IFRS update — November cohort')}
                  style={field}
                />
              </label>

              <label style={{ flex: '0 1 150px', minWidth: 0 }}>
                <span style={label}>{t('Type')}</span>
                <select
                  value={editing.kind}
                  onChange={(e) =>
                    setEditing({ ...editing, kind: e.target.value as 'public' | 'corporate' })
                  }
                  style={field}
                >
                  <option value="public">{t('Public course')}</option>
                  <option value="corporate">{t('Corporate booking')}</option>
                </select>
              </label>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
              <label style={{ flex: '0 1 160px', minWidth: 0 }}>
                <span style={label}>{t('Starts')}</span>
                <input
                  type="date"
                  value={editing.startsOn}
                  onChange={(e) => setEditing({ ...editing, startsOn: e.target.value })}
                  style={field}
                />
              </label>

              <label style={{ flex: '0 1 160px', minWidth: 0 }}>
                <span style={label}>
                  {t('Ends')}{' '}
                  <span style={{ fontWeight: 400, color: theme.textMuted }}>
                    ({t('same day if blank')})
                  </span>
                </span>
                <input
                  type="date"
                  value={editing.endsOn ?? ''}
                  min={editing.startsOn}
                  onChange={(e) =>
                    setEditing({ ...editing, endsOn: e.target.value === '' ? null : e.target.value })
                  }
                  style={field}
                />
              </label>

              <label style={{ flex: '1 1 200px', minWidth: 0 }}>
                <span style={label}>{t('Trainer')}</span>
                <input
                  value={editing.trainer ?? ''}
                  onChange={(e) => setEditing({ ...editing, trainer: e.target.value })}
                  style={field}
                />
              </label>

              <label style={{ flex: '1 1 200px', minWidth: 0 }}>
                <span style={label}>{t('Venue')}</span>
                <input
                  value={editing.venue ?? ''}
                  onChange={(e) => setEditing({ ...editing, venue: e.target.value })}
                  style={field}
                />
              </label>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
              <label style={{ flex: '0 1 150px', minWidth: 0 }}>
                <span style={label}>
                  {t('Seats')}{' '}
                  <span style={{ fontWeight: 400, color: theme.textMuted }}>
                    ({t('blank = no limit')})
                  </span>
                </span>
                <input
                  value={editing.capacity === null ? '' : String(editing.capacity)}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      capacity:
                        e.target.value.trim() === '' ? null : Math.round(parseAmount(e.target.value)),
                    })
                  }
                  inputMode="numeric"
                  dir="ltr"
                  style={{ ...field, fontFamily: theme.fontMono }}
                />
              </label>

              {/* Only for a corporate booking: a company agrees one price for
                  the whole engagement, and the heads on the day do not change
                  it. A public cohort is priced per seat, on the registration. */}
              {corporate && (
                <label style={{ flex: '0 1 200px', minWidth: 0 }}>
                  <span style={label}>{t('Agreed contract fee')}</span>
                  <input
                    value={editing.contractFee === null ? '' : String(editing.contractFee)}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        contractFee:
                          e.target.value.trim() === '' ? null : parseAmount(e.target.value),
                      })
                    }
                    inputMode="decimal"
                    dir="ltr"
                    style={{ ...field, fontFamily: theme.fontMono }}
                  />
                </label>
              )}

              <label style={{ flex: '0 1 160px', minWidth: 0 }}>
                <span style={label}>{t('Status')}</span>
                <select
                  value={editing.status}
                  onChange={(e) =>
                    setEditing({ ...editing, status: e.target.value as DeliveryInput['status'] })
                  }
                  style={field}
                >
                  <option value="planned">{t('Planned')}</option>
                  <option value="running">{t('Running')}</option>
                  <option value="finished">{t('Finished')}</option>
                  <option value="cancelled">{t('Cancelled')}</option>
                </select>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '9px' }}>
              <button
                type="button"
                disabled={!ready}
                onClick={() => {
                  onSave(editing);
                  setEditing(null);
                }}
                style={{
                  height: '38px',
                  minWidth: '150px',
                  background: ready ? theme.nav : theme.borderStrong,
                  color: '#fff',
                  border: 0,
                  borderRadius: theme.radiusControl,
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: ready ? 'pointer' : 'default',
                }}
              >
                {busy ? t('Saving…') : t('Save')}
              </button>

              <button
                type="button"
                onClick={() => setEditing(null)}
                style={{
                  height: '38px',
                  padding: '0 16px',
                  background: 'transparent',
                  border: `1px solid ${theme.borderStrong}`,
                  borderRadius: theme.radiusControl,
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t('Cancel')}
              </button>
            </div>
          </div>
        </Card>
      )}

      <Card>
        {loading && (
          <div style={{ padding: '18px', fontSize: '13px', color: theme.textMuted }}>
            {t('Loading…')}
          </div>
        )}

        {!loading && deliveries.length === 0 && (
          <div style={{ padding: '18px', fontSize: '13px', color: theme.textMuted }}>
            {t('Nothing scheduled yet.')}
          </div>
        )}

        {!loading && deliveries.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={heading}>{t('Training delivery')}</th>
                <th style={heading}>{t('Dates')}</th>
                <th style={heading}>{t('Type')}</th>
                <th style={{ ...heading, textAlign: 'end' }}>{t('On the course')}</th>
                <th style={heading}>{t('Status')}</th>
                <th style={{ ...heading, width: '90px' }} />
              </tr>
            </thead>
            <tbody>
              {deliveries.map((delivery) => {
                const pill = PHASE_PILL[delivery.phase] ?? PHASE_PILL.upcoming!;
                return (
                  <tr key={delivery.deliveryId}>
                    <td style={cell}>
                      <div style={{ fontWeight: 600 }}>{delivery.name}</div>
                      <div style={{ color: theme.textMuted, fontSize: '11.5px' }}>
                        {delivery.courseName}
                        {delivery.trainer ? ` · ${delivery.trainer}` : ''}
                      </div>
                    </td>
                    <td style={{ ...cell, fontFamily: theme.fontMono, fontSize: '12px' }} dir="ltr">
                      {delivery.startsOn}
                      {delivery.endsOn && delivery.endsOn !== delivery.startsOn
                        ? ` → ${delivery.endsOn}`
                        : ''}
                    </td>
                    <td style={cell}>
                      {delivery.kind === 'corporate' ? t('Corporate') : t('Public')}
                    </td>
                    {/*
                      Left to right even on an Arabic page. "4 / 20" in an
                      RTL cell renders as "20 / 4", which says the opposite
                      of what it means — four seats on a course of twenty.
                    */}
                    <td
                      style={{ ...cell, textAlign: 'end', fontFamily: theme.fontMono }}
                      dir="ltr"
                    >
                      {delivery.liveRegistrations}
                      {delivery.capacity !== null && (
                        <span style={{ color: theme.textMuted }}>{` / ${delivery.capacity}`}</span>
                      )}
                    </td>
                    <td style={cell}>
                      {/* Read from the calendar, not from the status somebody
                          last set. A cohort whose dates passed six weeks ago
                          should not still say "planned". */}
                      <Pill status={pill.status} label={t(pill.label)} />
                    </td>
                    <td style={{ ...cell, textAlign: 'end' }}>
                      <button
                        type="button"
                        onClick={() => onOpen(delivery.deliveryId)}
                        style={{
                          background: 'transparent',
                          border: `1px solid ${theme.border}`,
                          borderRadius: theme.radiusControl,
                          padding: '5px 12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {t('Open')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
